/* ============================================================================
   FORMATO DE NÚMEROS QUE PUEDEN NO EXISTIR

   ══════════════════════════════════════════════════════════════════════════
   EL ERROR QUE TRAJO ESTE MÓDULO

       null is not an object (evaluating 'P.elevFt.toLocaleString')

   El piloto importó un KMZ para planificar un vuelo y la aplicación se fue a
   la pantalla de error. La causa estaba a dos pasos de distancia:

   · Un punto de KML **no trae cota**. La altitud del archivo se descarta a
     propósito: en un KML de Google Earth suele ser cero o un valor de dibujo,
     no una cota medida.
   · Al llevarlo a la ruta se consulta el modelo de terreno. **Si no hay red,
     `elevFt` queda en `null`** — que es el dato honesto: no se sabe.

   Hasta ahí todo bien. Lo que fallaba era la pantalla: seis lugares hacían
   `wp.elevFt.toLocaleString()` sin preguntar, y en castellano eso es afirmar
   que el número existe.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ NO SE ARREGLA PONIENDO CERO

   Era la salida de una línea, y es la peor: **un helipuerto a 2850 ft entraría
   como si estuviera al nivel del mar**, los márgenes OGE saldrían holgados por
   más de 800 m de error, y la pantalla mostraría «0 ft» con la misma
   tipografía con la que muestra un dato medido.

   La regla del proyecto es anterior a este error y no se negocia: **sin dato
   va un guion, nunca un cero.** Lo que faltaba era un solo lugar donde eso
   estuviera escrito, en vez de veinte lugares donde recordarlo.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTE MÓDULO NO HACE

   No decide si un dato falta: eso ya lo decidió quien lo guardó. Acá solo se
   escribe. Y **no convierte**: un texto que parece número no se formatea como
   número, porque si el dato llegó como texto hay un problema aguas arriba que
   taparlo esconde.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/** Lo que se ve cuando no hay dato. Un guion, no un cero y no un vacío. */
export const SIN_DATO = "—";

/**
 * ¿Hay un número de verdad acá?
 *
 * `Number.isFinite` y no `Number(x)`: **`Number(null)` es CERO y es finito.**
 * Este proyecto se quemó cinco veces con eso —una coordenada ausente que se
 * tomaba por el ecuador, un rumbo ausente que giraba el mapa al norte— así que
 * acá se comprueba el valor crudo, sin convertir nada.
 *
 * Un texto queda afuera a propósito, aunque parezca un número: si el dato llegó
 * como `"2850"` el problema está donde se guardó, y formatearlo acá lo
 * escondería.
 */
export function hayNumero(valor) {
  return typeof valor === "number" && Number.isFinite(valor);
}

/**
 * El número con la convención del castellano: **coma decimal, punto de miles**.
 *
 * Escrito a mano y no con `toLocaleString()`, a propósito. Esa función toma el
 * idioma de la máquina donde corre: la misma pantalla mostraba `12,0 NM` en una
 * computadora y `12.0 NM` en otra, y las pruebas pasaban o fallaban según dónde
 * se las corriera. Peor todavía: un Node compilado con `small-icu` —sin datos
 * de idioma— devuelve el formato inglés **en silencio**, así que el error no
 * aparece hasta que un piloto lee el número.
 *
 * Acá la convención es del proyecto, no del equipo. Un número que se lee en
 * cabina tiene que leerse igual en todos los iPad.
 */
function conSeparadores(valor, decimales) {
  const signo = valor < 0 ? "-" : "";
  const texto = decimales === null ? String(Math.abs(valor)) : Math.abs(valor).toFixed(decimales);

  /* Notación exponencial (números enormes o diminutos): no hay enteros que
     agrupar y el exponente se deja como está. No se da en esta aplicación
     —altitudes, distancias, pesos, coordenadas— pero devolver algo raro sin
     avisar sería peor que devolverlo tal cual. */
  if (/e/i.test(texto)) return String(valor).replace(".", ",");

  const [entera, fraccion] = texto.split(".");
  const agrupada = (entera ?? "0").replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${signo}${agrupada}${fraccion ? `,${fraccion}` : ""}`;
}

/**
 * Un número con separadores de miles, o el guion.
 *
 * `decimales` fija cuántos se muestran. Sin él, se muestra como venga.
 */
export function numero(valor, { decimales = null } = {}) {
  if (!hayNumero(valor)) return SIN_DATO;
  return conSeparadores(valor, decimales);
}

/**
 * Un número con su unidad, o el guion SOLO.
 *
 * Sin dato no se escribe la unidad: «— ft» se lee como si hubiera un valor de
 * cero pies. El guion solo es inequívoco.
 */
export function conUnidad(valor, unidad, opciones) {
  if (!hayNumero(valor)) return SIN_DATO;
  return `${numero(valor, opciones)} ${unidad}`;
}

/** Pies. Es el caso que trajo el error, y el que más aparece. */
export const pies = (valor) => conUnidad(valor, "ft");

/** Grados Celsius. */
export const grados = (valor) => conUnidad(valor, "°C");

/** Nudos. */
export const nudos = (valor) => conUnidad(valor, "kt");

/** Kilos. */
export const kilos = (valor) => conUnidad(valor, "kg", { decimales: 0 });

/** Millas náuticas, con un decimal: es como se lee una distancia en cabina. */
export const millas = (valor) => conUnidad(valor, "NM", { decimales: 1 });

/**
 * Una coordenada decimal, o el guion.
 *
 * Se usa donde todavía no se migró a grados, minutos y segundos. Para lo que
 * el piloto lee, el módulo es `coordenadas.js`.
 */
export const coordenada = (valor, decimales = 4) => numero(valor, { decimales });

/**
 * Un rumbo de tres dígitos: `007°`, no `7°`.
 *
 * Tres dígitos es como se dice un rumbo por radio, y como lo escribe la carta.
 */
export function rumbo(valor) {
  if (!hayNumero(valor)) return SIN_DATO;
  const normalizado = ((valor % 360) + 360) % 360;
  return `${String(Math.round(normalizado) % 360).padStart(3, "0")}°`;
}

/**
 * El nombre de un punto cuando no tiene uno.
 *
 * Vive acá porque arma el nombre con `toFixed` sobre coordenadas, que es el
 * mismo accidente de una línea más allá: con `lat` en `null` reventaba igual
 * que `elevFt`. **Sin coordenadas devuelve null**, no un «PTO —,—» que
 * después se guardaría como si fuera un nombre.
 */
export function nombreDePunto(lat, lon, decimales = 2) {
  if (!hayNumero(lat) || !hayNumero(lon)) return null;
  return `PTO ${lat.toFixed(decimales)},${lon.toFixed(decimales)}`;
}
